import { CommonPagination } from "@/components/common/CommonPagination";
import { ConfirmationDialog } from "@/components/common/ConfirmationDialog";
import Loading from "@/components/common/Loading";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import { QUERY_KEY } from "@/constant/queryKey";
import {
  useCreateAdminApiKey,
  useDeleteAdminApiKey,
} from "@/hooks/services/use-api-key";
import { useDebounce } from "@/hooks/use-debounce";
import { usePagination } from "@/hooks/use-pagination";
import { useUrlParams } from "@/hooks/use-url-params";
import {
  ADMIN_API_KEY_ENDPOINT,
  type AdminApiKey,
} from "@/services/core/admin-api-key.api";
import { useDialogStore } from "@/store/dialog-store";
import { useMerchantStore } from "@/store/merchant-store";
import { format } from "date-fns";
import { Copy, Plus, Search, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const ApiKeysPage = () => {
  const { params, updateParams } = useUrlParams();
  const [searchInput, setSearchInput] = useState(params.q);
  const debouncedSearch = useDebounce(searchInput, 500);
  const { selectedMerchant } = useMerchantStore();

  const {
    data: apiKeys,
    isLoading,
    isError,
    meta,
    setPage,
    setSearch,
    setLimit,
  } = usePagination<AdminApiKey>({
    endpoint: `${ADMIN_API_KEY_ENDPOINT}/${selectedMerchant?.name}`,
    queryKey: [QUERY_KEY.ADMIN_API_KEY, selectedMerchant?.name ?? ""],
    initialParams: {
      currentPage: params.currentPage,
      pageSize: params.pageSize,
      s: params.s,
      q: params.q,
    },
  });

  console.log("api keys", apiKeys);

  const [selectedAdminApiKey, setSelectedAdminApiKey] =
    useState<AdminApiKey | null>(null);

  const { deleteDialog, openDeleteDialog, closeDeleteDialog } =
    useDialogStore();

  const deleteMutation = useDeleteAdminApiKey();
  const createMutation = useCreateAdminApiKey();

  const handleAdminDeleteSubmit = async () => {
    console.log(`Deleting item with ID: ${selectedAdminApiKey?.id}`);
    if (!selectedAdminApiKey) return;
    try {
      await deleteMutation.mutateAsync({
        username: selectedMerchant?.name ?? "",
        id: selectedAdminApiKey?.id,
      });

      toast.success("Delete api key successful!");
    } catch (error) {
      console.error("Failed to create api key:", error);
    }
  };

  const handleAdminCreateSubmit = async () => {
    if (!selectedMerchant) return;

    try {
      await createMutation.mutateAsync({ username: selectedMerchant.name });

      toast.success("Create new api key successful!");
    } catch (error) {
      console.error("Failed to create new api key: ", error);
    }
  };

  const handlePageChange = (page: number) => {
    updateParams({ currentPage: page });
    setPage(page);
  };

  const handleLimitChange = (limit: number) => {
    updateParams({ pageSize: limit, currentPage: 1 });
    setLimit(limit);
  };

  const handleSortChange = (sort: string) => {
    console.log("sort", sort);
    updateParams({ s: sort, currentPage: 1 });
  };

  const handleSearch = (value: string) => {
    const trimmedValue = value.trim();
    setSearchInput(value);

    if (trimmedValue !== params.q) {
      updateParams({ q: trimmedValue, currentPage: 1 });
    }
  };

  useEffect(() => {
    updateParams({ q: debouncedSearch, currentPage: 1 });
    setSearch(debouncedSearch || "");
    setPage(1);
  }, [debouncedSearch]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("API Key copied to clipboard");
  };

  return (
    <div className="w-full space-y-4">
      <div className="flex w-full items-center justify-between gap-4 sm:flex-row sm:items-center">
        <div className="relative w-1/2">
          <Search className="text-muted-foreground absolute top-2.5 left-2.5 h-4 w-4" />
          <Input
            type="text"
            placeholder="Search keys..."
            className="pl-8"
            value={searchInput}
            onChange={(e) => handleSearch(e.target.value)}
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput("")}
              className="text-muted-foreground hover:text-foreground absolute top-2.5 right-2 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none"
            >
              <X className="h-4 w-4 cursor-pointer" />
              <span className="sr-only">Clear search</span>
            </button>
          )}
        </div>

        <div className="flex w-full items-center justify-end gap-2">
          <Button
            onClick={handleAdminCreateSubmit}
            className="cursor-pointer"
            disabled={createMutation.isPending}
          >
            <Plus className="mr-2 h-4 w-4" />
            {createMutation.isPending ? "Creating..." : "Create Api Key"}
          </Button>

          <Select defaultValue={params.s} onValueChange={handleSortChange}>
            <SelectTrigger className="w-42">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tableId:asc">ID (Asc)</SelectItem>
              <SelectItem value="tableId:desc">ID (Desc)</SelectItem>
              <SelectItem value="name:asc">Name (A-Z)</SelectItem>
              <SelectItem value="name:desc">Name (Z-A)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-full items-center justify-center">
          <Loading />
        </div>
      ) : isError ? (
        <div className="py-8 text-center text-red-500">
          Error loading api keys
        </div>
      ) : (
        <>
          {apiKeys.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead className="w-[450px]">Token</TableHead>
                    <TableHead>Created By</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {apiKeys.map((apiKey, idx) => {
                    const tableId =
                      (meta.currentPage - 1) * meta.pageSize + idx + 1;
                    return (
                      <TableRow key={apiKey.id}>
                        <TableCell className="font-medium">{tableId}</TableCell>
                        <TableCell className="font-mono">
                          <div className="flex items-center gap-2">
                            <span className="max-w-[350px] truncate">
                              {apiKey.token}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => copyToClipboard(apiKey.token)}
                            >
                              <Copy className="h-4 w-4" />
                              <span className="sr-only">Copy token</span>
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>{apiKey.createdBy}</TableCell>
                        <TableCell>
                          {format(
                            new Date(apiKey.createdAt),
                            "dd MMM yyyy HH:mm",
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="destructive"
                            size="icon"
                            className="cursor-pointer"
                            onClick={() => {
                              openDeleteDialog();
                              setSelectedAdminApiKey(apiKey);
                            }}
                            aria-label="Delete API Key"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex h-42 items-center justify-center">
              <p>There is no data</p>
            </div>
          )}

          {meta && (
            <CommonPagination
              page={meta.currentPage}
              totalPages={meta.totalPages}
              limit={meta.pageSize}
              hasNextPage={meta.hasNextPage}
              hasPrevPage={meta.hasPreviousPage}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
              limitOptions={[10, 25, 50, 100]}
              showLimitSelector={true}
            />
          )}
        </>
      )}

      {/* Delete Dialog */}
      <ConfirmationDialog
        open={deleteDialog}
        onOpenChange={closeDeleteDialog}
        onConfirm={handleAdminDeleteSubmit}
        confirmText={deleteMutation.isPending ? "Deleting..." : "Delete"}
        title="Deleting API Key?"
        description="This action cannot be undone. This will permanently delete your api key from our servers."
        loading={deleteMutation.isPending}
      />
    </div>
  );
};

export default ApiKeysPage;
